import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react-native';
import { Platform, View } from 'react-native';
import { usePublicStatus, type PublicStatusNotice, type PublicStatusState } from './usePublicStatus';

const DISMISSED_PREFIX = 'radious:status-notice-dismissed:';

function noticeForStatus(status: PublicStatusState): PublicStatusNotice | null {
  if (status === 'ok') {
    return null;
  }

  return {
    id: `status-${status}`,
    type: 'status',
    severity: status,
    title: status === 'down' ? 'Some Radious features are unavailable' : 'Radious is slower than usual',
    message:
      status === 'down'
        ? 'We are working on it now. Some pages or actions may fail until service recovers.'
        : 'Radious is open, but some pages or actions may be delayed.',
    affected_components: [],
  };
}

function storageKey(notice: PublicStatusNotice) {
  return `${DISMISSED_PREFIX}${notice.id}`;
}

export function StatusNoticeBanner({ enabled = true }: { enabled?: boolean }) {
  const statusQuery = usePublicStatus(enabled);
  const notice = statusQuery.data?.public_notice ?? noticeForStatus(statusQuery.data?.status ?? 'ok');
  const [dismissedKey, setDismissedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!notice || Platform.OS !== 'web') {
      setDismissedKey(null);
      return;
    }

    const key = storageKey(notice);
    setDismissedKey(window.sessionStorage.getItem(key) === 'true' ? key : null);
  }, [notice]);

  if (!enabled || Platform.OS !== 'web' || !notice || dismissedKey === storageKey(notice)) {
    return null;
  }

  const isUrgent = notice.severity === 'down' || notice.severity === 'outage';
  const label = notice.upcoming
    ? 'Scheduled maintenance'
    : notice.type === 'maintenance'
      ? 'Maintenance'
      : isUrgent
        ? 'Service alert'
        : 'Status';

  return (
    <View
      className={cn(
        'web:sticky web:top-0 web:z-50 border-b px-4 py-2',
        isUrgent ? 'border-destructive/30 bg-destructive/15' : 'border-border bg-background/95'
      )}
    >
      <View className="mx-auto flex w-full max-w-7xl flex-row items-center gap-3">
        <View className="min-w-0 flex-1 flex-row flex-wrap items-center gap-x-2 gap-y-1">
          <Text className={cn('text-xs font-bold uppercase', isUrgent ? 'text-destructive' : 'text-primary')}>
            {label}
          </Text>
          <Text className="text-sm font-semibold text-foreground">{notice.title}</Text>
          {notice.message ? (
            <Text className="text-sm text-muted-foreground">{notice.message}</Text>
          ) : null}
        </View>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onPress={() => {
            const key = storageKey(notice);
            window.sessionStorage.setItem(key, 'true');
            setDismissedKey(key);
          }}
        >
          <Icon as={X} className="size-4 text-muted-foreground" />
        </Button>
      </View>
    </View>
  );
}
